import { z } from 'zod';

/** Where a photo came from. `glean` = uploaded by the media team in this app; `flock` = posted in the Flock app. */
export const Source = z.enum(['glean', 'flock']);
export type Source = z.infer<typeof Source>;

/** What the source dropdown scopes to. */
export const SourceScope = z.enum(['glean', 'flock', 'all']);
export type SourceScope = z.infer<typeof SourceScope>;

/** Photo shape after EXIF rotation. */
export const Orient = z.enum(['h', 'v', 'sq']);
export type Orient = z.infer<typeof Orient>;

/**
 * Tag categories. Groups come in two kinds: `fellowship` (Fathers, Mothers, Young Adults, Seniors… — a person belongs
 * to at most one) and `service` (Choir, Translation, Media, AV, Church School, News Letter, Cafe… — a person can serve in any number).
 * Both are fixed lists (seeded; managed outside the app), like churches and regions.
 */
export const TagCategory = z.enum(['year', 'region', 'event', 'church', 'fellowship', 'service', '']);
export type TagCategory = z.infer<typeof TagCategory>;

/**
 * Who can see a photo. `unreleased` = only in Studio (the default for every upload), `released` = every active
 * member, `restricted` = people who may see restricted photos (never shareable).
 */
export const Visibility = z.enum(['unreleased', 'released', 'restricted']);
export type Visibility = z.infer<typeof Visibility>;

export const UserStatus = z.enum(['pending', 'active', 'suspended']);
export type UserStatus = z.infer<typeof UserStatus>;

export const Region = z.enum(['East', 'Central', 'West']);
export type Region = z.infer<typeof Region>;

/** A tag is lowercase kebab text without the leading '#'. */
export const TagName = z
  .string()
  .min(1)
  .max(48)
  .regex(/^[a-z0-9][a-z0-9-]*$/, 'tags are lowercase letters, numbers and dashes');

export const Tag = z.object({
  name: TagName,
  category: TagCategory,
  label: z.string().min(1).max(64),
  aliases: z.array(z.string()).default([]),
  /** Church entries: the id in the production `churches` table (the church list's source of truth) and the church's region. */
  churchId: z.string().optional(),
  region: Region.optional(),
});
export type Tag = z.infer<typeof Tag>;

/** What someone can do in Studio across Glean (see `User.type`). */
export const UserType = z.enum(['user', 'media']);
export type UserType = z.infer<typeof UserType>;

/**
 * The shared JBCH USA profile (table `jbchusa-users`, one per Cognito sub), used by every JBCH USA app. Holds who the
 * person is; what they may do in Photos lives in their `Membership`.
 */
export const Profile = z.object({
  id: z.string(), // Cognito sub
  /** Lowercased; unique across profiles. */
  email: z.string().email(),
  name: z.string().min(1).max(80),
  /** Optional, E.164. Not verified, no SMS. */
  phone: z.string().max(16).nullable().default(null),
  /**
   * Whether this profile holds the unique `PHONE#` lookup for its phone. Only approved members claim it (at approval, or
   * when they change it later), so someone not yet approved can't block or take over another person's number.
   */
  phoneClaimed: z.boolean().default(false),
  region: Region.optional(),
  /** Church tag name, e.g. 'new-york' ('' = not set). */
  church: z.string().max(64).default(''),
  /** Fellowship group tag (at most one), e.g. 'young-adults'. */
  fellowship: z.string().max(48).nullable().default(null),
  /** Service group tags (any number), e.g. ['choir', 'translation']. */
  services: z.array(z.string().max(48)).max(20).default([]),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Profile = z.infer<typeof Profile>;

/** Someone's Photos membership (photos table): their access to this app and its switches. */
export const Membership = z.object({
  userId: z.string(),
  /** Photos access only; suspending here leaves the shared profile and other apps alone. */
  access: UserStatus,
  requestReason: z.string().max(280).default(''),
  requestedAt: z.string(),
  /** The admin who last approved, suspended or reactivated them. */
  decidedBy: z.string().optional(),
  decidedAt: z.string().optional(),
  type: z.enum(['user', 'media']).default('user'),
  canRelease: z.boolean().default(false),
  canSeeRestricted: z.boolean().default(false),
  isAdmin: z.boolean().default(false),
  lastActiveAt: z.string().optional(),
});
export type Membership = z.infer<typeof Membership>;

/** A Photos user as the API returns it: the shared profile plus the Photos membership (`status` = `access`). */
export const User = z.object({
  id: z.string(), // Cognito sub
  email: z.string().email(),
  name: z.string().min(1).max(80),
  /** Church tag name, e.g. 'new-york' ('' = not set). */
  church: z.string().max(64).default(''),
  region: Region.optional(),
  /** Fellowship group tag (at most one), e.g. 'young-adults'. */
  fellowship: z.string().max(48).nullable().default(null),
  /** Service group tags (any number), e.g. ['choir', 'translation']. */
  services: z.array(z.string().max(48)).max(20).default([]),
  /** Optional phone in E.164 (e.g. '+12015550123'); not verified, no SMS. */
  phone: z.string().max(16).nullable().default(null),
  status: UserStatus,
  /**
   * `media`: upload, tag and organize in every album (Studio). `user`: none of that, except in albums they were given
   * access to (an invite link or an admin grant).
   */
  type: UserType.default('user'),
  /** Release / unrelease. Media only (always false for type user; the API enforces it). */
  canRelease: z.boolean().default(false),
  /** See restricted photos (and mark photos restricted, if media). */
  canSeeRestricted: z.boolean().default(false),
  /** The Admin area. Independent: implies neither media nor any switch above. */
  isAdmin: z.boolean().default(false),
  requestReason: z.string().max(280).default(''),
  createdAt: z.string(),
  lastActiveAt: z.string().optional(),
});
export type User = z.infer<typeof User>;

export const PhotoStatus = z.enum(['processing', 'ready', 'trashed', 'failed']);
export type PhotoStatus = z.infer<typeof PhotoStatus>;

/** Decimal degrees (south / west negative) and meters (below sea level negative). */
export const GeoLocation = z.object({ lat: z.number().min(-90).max(90), lon: z.number().min(-180).max(180), alt: z.number().optional() });
export type GeoLocation = z.infer<typeof GeoLocation>;

export const Photo = z.object({
  id: z.string(),
  source: Source,
  /** Set when a Flock photo was added to Glean ("Add to Glean"). */
  origin: Source.optional(),
  visibility: Visibility,
  status: PhotoStatus,
  tags: z.array(TagName),
  takenAt: z.string(), // ISO; EXIF capture time, falls back to upload time
  uploadedAt: z.string(),
  uploaderId: z.string(),
  uploaderName: z.string(),
  /** One upload session (used in the `incoming/<uploadId>/<photoId>` key). */
  uploadId: z.string(),
  /** Glean photos: the album the photo lives in (where it was uploaded; "Move to album" changes it). */
  homeAlbumId: z.string().optional(),
  /** Flock posts: the poster's caption. */
  caption: z.string().max(280).optional(),
  fileName: z.string(),
  contentType: z.string(),
  bytes: z.number().int().nonnegative(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  orient: Orient,
  sha256: z.string().regex(/^[a-f0-9]{64}$/),
  camera: z.string().optional(),
  thumbhash: z.string().optional(),
  /** 64-bit perceptual hash (dHash), 16 hex digits: finds near-duplicates (re-encodes, resizes). */
  phash: z.string().regex(/^[0-9a-f]{16}$/).optional(),
  /** EXIF GPS position. Only Studio returns it, and only to media: never Browse, Flock or share links. */
  location: GeoLocation.optional(),
  /** "Add as a separate copy": the photo this one was copied from. Copies never claim the sha256 (upload dedupe checks originals). */
  copiedFrom: z.string().optional(),
  picked: z.boolean().default(false),
  /** S3 keys */
  originalKey: z.string(),
  thumbKey: z.string().optional(),
  previewKey: z.string().optional(),
  trashedAt: z.string().optional(),
  /** Who moved it to Trash (or asked for its removal). */
  trashedBy: z.string().optional(),
  trashedByName: z.string().optional(),
  /** Why processing failed, e.g. 'duplicate of <id>' or 'unsupported format'. */
  failureReason: z.string().optional(),
});
export type Photo = z.infer<typeof Photo>;

export const AlbumName = z.string().trim().min(1).max(100);

/** An album organizes Glean photos in Studio. Photos live in one home album and can be added to others. */
export const Album = z.object({
  id: z.string(),
  name: AlbumName,
  description: z.string().max(500).default(''),
  /** Tags every upload into this album starts with (removable per upload); "Update tags" on move/add uses them too. */
  defaultTags: z.array(TagName).max(30).default([]),
  createdBy: z.string(),
  createdByName: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Album = z.infer<typeof Album>;

/** Someone who joined an album through an invite link: may upload and tag in that album only. */
export const Collaborator = z.object({
  albumId: z.string(),
  userId: z.string(),
  name: z.string(),
  joinedAt: z.string(),
  /** The invite's non-secret `ref` when they joined through an invite link; otherwise they were added directly. */
  inviteRef: z.string().optional(),
  /** Who gave the access: the person who added them, or who created the invite link. */
  addedBy: z.string().optional(),
  addedByName: z.string().optional(),
});
export type Collaborator = z.infer<typeof Collaborator>;

/** Album invite link; `id` is the secret token in `/join/<id>` and is only ever shown to media. */
export const Invite = z.object({
  id: z.string(),
  /** Non-secret reference to this invite (kept on collaborators who joined through it). */
  ref: z.string(),
  albumId: z.string(),
  createdBy: z.string(),
  createdByName: z.string(),
  createdAt: z.string(),
  expiresAt: z.string(),
  revokedAt: z.string().optional(),
});
export type Invite = z.infer<typeof Invite>;

/** Album access taken away from someone: invite links created before `at` no longer let them back in. */
export const AccessBlock = z.object({ albumId: z.string(), userId: z.string(), at: z.string() });
export type AccessBlock = z.infer<typeof AccessBlock>;

export const ActivityAction = z.enum([
  'album.create',
  'album.update',
  'album.delete',
  'photos.upload',
  'photos.release',
  'photos.unrelease',
  'photos.restrict',
  'photos.unrestrict',
  'photos.move',
  'photos.add',
  'photos.copy',
  'photos.remove',
  'photos.tag',
  'photos.pick',
  'photos.trash',
  'photos.glean',
  'photos.restore',
  'photos.delete',
  'invite.create',
  'invite.join',
  'invite.revoke',
  'collaborator.add',
  'collaborator.remove',
  'undo',
]);
export type ActivityAction = z.infer<typeof ActivityAction>;

/** The fields of one photo that an activity changed (only the fields it touched are present). */
export const PhotoState = z.object({
  visibility: Visibility.optional(),
  tags: z.array(TagName).optional(),
  picked: z.boolean().optional(),
  trashed: z.boolean().optional(),
  homeAlbumId: z.string().optional(),
  /** Membership in the undo record's `albumId`. */
  inAlbum: z.boolean().optional(),
});
export type PhotoState = z.infer<typeof PhotoState>;

/** What an undo needs: each photo's state before and after the change. */
export const UndoRecord = z.object({
  albumId: z.string().optional(),
  before: z.record(PhotoState),
  after: z.record(PhotoState),
});
export type UndoRecord = z.infer<typeof UndoRecord>;

/** One entry of the Studio activity log. */
export const Activity = z.object({
  id: z.string(),
  at: z.string(),
  actorId: z.string(),
  actorName: z.string(),
  action: ActivityAction,
  albumId: z.string().optional(),
  albumName: z.string().optional(),
  photoIds: z.array(z.string()),
  /** e.g. "Daniel Kim released 40 photos in Summer Retreat 2026" */
  summary: z.string(),
  undo: UndoRecord.optional(),
  /** Set once the entry was undone. */
  undone: z.object({ by: z.string(), byName: z.string(), at: z.string(), activityId: z.string() }).optional(),
  /** On `undo` entries: the entry that was undone. */
  undoOf: z.string().optional(),
});
export type Activity = z.infer<typeof Activity>;
